import React, { useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldBan, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Ban = no marketing of any kind, no online booking (mobile + email), upcoming appointments cancelled quietly.
export const BanClient = ({ client, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const banned = !!client?.banned;

  const ban = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/ban`, { reason });
      toast.success(`${client.name} banned${data.cancelled ? ` · ${data.cancelled} upcoming appointment${data.cancelled === 1 ? "" : "s"} cancelled` : ""}`);
      setOpen(false); setReason(""); onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const unban = async () => {
    setBusy(true);
    try { await api.post(`/clients/${client.id}/unban`); toast.success("Ban lifted"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (banned) return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex flex-wrap items-center justify-between gap-3" data-testid="file-banned-box">
      <div className="flex items-start gap-2">
        <ShieldBan className="h-4 w-4 text-red-600 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-red-700">Banned from this business</div>
          <div className="text-xs text-red-700/80">Since {(client.banned_at || "").slice(0, 10)}{client.banned_by ? ` · by ${client.banned_by}` : ""}{client.banned_reason ? ` · ${client.banned_reason}` : ""}. No marketing, no online booking, portal sign-in refused.</div>
        </div>
      </div>
      <Button variant="outline" size="sm" className="rounded-lg" onClick={unban} disabled={busy} data-testid="file-unban-btn"><ShieldCheck className="h-3.5 w-3.5 mr-1" />Lift ban</Button>
    </div>
  );

  return (
    <>
      <div className="rounded-lg border p-3 flex flex-wrap items-center justify-between gap-3" data-testid="file-ban-box">
        <div className="flex items-start gap-2">
          <ShieldBan className="h-4 w-4 text-muted-foreground mt-0.5" />
          <div>
            <div className="text-sm font-medium">Ban from this business</div>
            <div className="text-xs text-muted-foreground">Removes them from all marketing, blocks online booking by mobile and email, cancels upcoming appointments quietly.</div>
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-lg border-red-200 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="file-ban-btn">Ban client</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="ban-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ban {client?.name}?</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              They'll be removed from every marketing list and automation, their mobile and email will be refused for online booking, waitlist and the client portal, and any upcoming appointments are cancelled without a text. If they try to book they'll see a firm, final message that the clinic won't be providing further services.
            </DialogDescription>
          </DialogHeader>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (for your team only)" className="rounded-lg" data-testid="ban-reason-input" />
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-lg" onClick={() => setOpen(false)} data-testid="ban-cancel-btn">Cancel</Button>
            <Button className="rounded-lg bg-red-600 hover:bg-red-700 text-white" onClick={ban} disabled={busy} data-testid="ban-confirm-btn">Ban client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
